import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { DocTypeBadge } from "./AttachmentBadges";
import { ACCEPT_ATTR, MAX_FILE_MB } from "@/lib/attachmentConstants";
import { entriesFromFileList, partitionEntries, pickFolder, supportsDirectoryPicker } from "@/lib/folderImport";
import { formatBytes, formatKr, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { FolderOpen, UploadCloud, Camera, FileText, Image as ImageIcon, FileCode2, CheckCircle2, AlertTriangle, CopyCheck, Loader2, Square, X, ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const iconFor = (name = "") => {
  const n = name.toLowerCase();
  if (n.endsWith(".pdf")) return FileText;
  if (n.endsWith(".xml")) return FileCode2;
  return ImageIcon;
};

/**
 * Import til bilagsbanken: velg mappe (rekursivt), velg filer, dra og slipp eller ta bilde.
 * Filene lastes opp én etter én (AI-lesing tar noen sekunder per fil) med samlet fremdrift.
 */
export const ImportPanel = ({ onImported, canWrite = true }) => {
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const cameraRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [analyze, setAnalyze] = useState(true);
  const [queue, setQueueState] = useState([]); // {id, file, path, status: venter|laster|ferdig|duplikat|feil, result, error}
  const queueRef = useRef([]); // speil av køen for den asynkrone opplastingsløkken
  const setQueue = useCallback((fn) => { queueRef.current = typeof fn === "function" ? fn(queueRef.current) : fn; setQueueState(queueRef.current); }, []);
  const [folderName, setFolderName] = useState("");
  const runningRef = useRef(false);
  const stopRef = useRef(false);
  const analyzeRef = useRef(true);
  useEffect(() => { analyzeRef.current = analyze; }, [analyze]);

  const pump = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    stopRef.current = false;
    // Kjør så lenge det finnes ventende filer (én om gangen – AI-lesing tar noen sekunder per fil)
    for (;;) {
      if (stopRef.current) break;
      const item = queueRef.current.find((x) => x.status === "venter");
      if (!item) break;
      setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "laster" } : x)));
      try {
        const fd = new FormData();
        fd.append("file", item.file, item.file.name);
        if (item.path && item.path !== item.file.name) fd.append("source_path", item.path);
        fd.append("analyze", analyzeRef.current ? "true" : "false");
        const res = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
        const { duplicate, attachment } = res.data;
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: duplicate ? "duplikat" : "ferdig", result: attachment } : x)));
        if (!duplicate) onImported?.(attachment);
      } catch (e) {
        setQueue((q) => q.map((x) => (x.id === item.id ? { ...x, status: "feil", error: errorMessage(e, "Kunne ikke laste opp") } : x)));
      }
    }
    runningRef.current = false;
  }, [onImported, setQueue]);

  const enqueue = (entries, label) => {
    const { ok, skipped } = partitionEntries(entries);
    if (skipped.unsupported || skipped.tooBig) {
      const parts = [];
      if (skipped.unsupported) parts.push(`${skipped.unsupported} med filtype vi ikke leser`);
      if (skipped.tooBig) parts.push(`${skipped.tooBig} større enn ${MAX_FILE_MB} MB`);
      toast(`Hoppet over ${parts.join(" og ")}.`, { id: "bilag-skipped" });
    }
    if (!ok.length) { if (!skipped.unsupported && !skipped.tooBig) toast("Ingen filer valgt."); return; }
    const items = ok.map((e) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file: e.file, path: e.path, status: "venter" }));
    setQueue((q) => [...q, ...items]);
    if (label) setFolderName(label);
    toast.success(`${ok.length} ${ok.length === 1 ? "fil" : "filer"} lagt i køen${label ? ` fra «${label}»` : ""}`, { id: "bilag-enqueued" });
    setTimeout(pump, 0);
  };

  const chooseFolder = async () => {
    if (supportsDirectoryPicker()) {
      const res = await pickFolder();
      if (res?.cancelled) return;
      if (res) { enqueue(res.entries, res.name); return; }
    }
    folderRef.current?.click();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!canWrite) return;
    enqueue(entriesFromFileList(e.dataTransfer.files));
  };

  const total = queue.length;
  const finished = queue.filter((q) => ["ferdig", "duplikat", "feil"].includes(q.status)).length;
  const active = queue.some((q) => q.status === "laster" || q.status === "venter");
  const pct = total ? Math.round((finished / total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="bilag-import-panel">
      <div
        role="button"
        tabIndex={0}
        onClick={() => canWrite && fileRef.current?.click()}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && canWrite) fileRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (canWrite) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn("rounded-2xl border-2 border-dashed bg-card p-6 sm:p-8 text-center transition-colors", canWrite ? "cursor-pointer" : "opacity-60 cursor-not-allowed", dragging && "dropzone-active")}
        data-testid="bilag-dropzone"
        aria-label="Dra og slipp bilag her, eller velg mappe eller filer"
      >
        <div className="mx-auto h-14 w-14 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center mb-3"><UploadCloud className="h-7 w-7" /></div>
        <p className="text-lg font-semibold">Dra og slipp bilag her</p>
        <p className="text-sm text-muted-foreground mt-1">Bilder og PDF av regninger, fakturaer og kvitteringer. Velg en hel mappe på maskinen din – undermapper tas med.</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button type="button" onClick={chooseFolder} disabled={!canWrite} data-testid="bilag-choose-folder"><FolderOpen className="h-4 w-4 mr-2" /> Velg mappe</Button>
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={!canWrite} data-testid="bilag-choose-files"><UploadCloud className="h-4 w-4 mr-2" /> Velg filer</Button>
          <Button type="button" variant="outline" className="sm:hidden" onClick={() => cameraRef.current?.click()} disabled={!canWrite} data-testid="bilag-camera"><Camera className="h-4 w-4 mr-2" /> Ta bilde</Button>
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><FileText className="h-3.5 w-3.5" /> PDF</span>
          <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><ImageIcon className="h-3.5 w-3.5" /> JPG / PNG / WEBP</span>
          <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><FileCode2 className="h-3.5 w-3.5" /> EHF / XML</span>
          <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1">Maks {MAX_FILE_MB} MB per fil</span>
        </div>
        <input ref={fileRef} type="file" accept={ACCEPT_ATTR} multiple className="hidden" onChange={(e) => { enqueue(entriesFromFileList(e.target.files)); e.target.value = ""; }} data-testid="bilag-file-input" />
        <input ref={folderRef} type="file" accept={ACCEPT_ATTR} multiple webkitdirectory="" directory="" className="hidden" onChange={(e) => { const list = e.target.files; const first = list?.[0]?.webkitRelativePath?.split("/")[0]; enqueue(entriesFromFileList(list), first || "mappe"); e.target.value = ""; }} data-testid="bilag-folder-input" />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { enqueue(entriesFromFileList(e.target.files)); e.target.value = ""; }} data-testid="bilag-camera-input" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <Switch checked={analyze} onCheckedChange={setAnalyze} aria-label="Les med AI automatisk" data-testid="bilag-analyze-switch" />
          <span className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Les og klassifiser automatisk med AI</span>
        </label>
        <p className="text-xs text-muted-foreground">Uten AI lagres filene som «Ikke lest», og du kan lese dem senere.</p>
      </div>

      {total ? (
        <Card data-testid="bilag-import-queue">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-medium" data-testid="bilag-import-progress-text">{active ? `Behandler ${finished + 1 > total ? total : finished + 1} av ${total}` : `${finished} av ${total} ferdig`}{folderName ? <span className="text-muted-foreground font-normal"> · fra «{folderName}»</span> : null}</p>
                <p className="text-xs text-muted-foreground">{queue.filter((q) => q.status === "ferdig").length} lagt inn · {queue.filter((q) => q.status === "duplikat").length} allerede i banken · {queue.filter((q) => q.status === "feil").length} feilet</p>
              </div>
              <div className="flex items-center gap-2">
                {active ? <Button variant="outline" size="sm" onClick={() => { stopRef.current = true; setQueue((q) => q.filter((x) => x.status !== "venter")); }} data-testid="bilag-import-stop"><Square className="h-3.5 w-3.5 mr-1.5" /> Stopp</Button> : null}
                {finished ? <Button variant="ghost" size="sm" onClick={() => setQueue((q) => q.filter((x) => !["ferdig", "duplikat"].includes(x.status)))} data-testid="bilag-import-clear"><X className="h-4 w-4 mr-1" /> Fjern ferdige</Button> : null}
              </div>
            </div>
            <Progress value={pct} aria-label="Fremdrift" data-testid="bilag-import-progress" />
            <ul className="divide-y max-h-80 overflow-auto -mx-1 px-1">
              {queue.map((item) => {
                const Icon = iconFor(item.file.name);
                const r = item.result;
                return (
                  <li key={item.id} className="flex items-start gap-3 py-2.5" data-testid={`bilag-queue-item-${item.status}`}>
                    <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" title={item.path}>{item.path || item.file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatBytes(item.file.size)}{item.status === "venter" ? " · venter" : ""}{item.status === "laster" ? (analyze ? " · laster opp og leser …" : " · laster opp …") : ""}</p>
                      {item.status === "feil" ? <p className="text-xs text-destructive mt-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {item.error}</p> : null}
                      {item.status === "duplikat" && r ? <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><CopyCheck className="h-3.5 w-3.5" /> Finnes allerede i bilagsbanken – <Link to={`/bilag/${r.id}`} className="underline" data-testid="bilag-queue-open-duplicate">åpne</Link></p> : null}
                      {item.status === "ferdig" && r ? (
                        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                          <DocTypeBadge docType={r.doc_type} short />
                          {r.fields?.counterparty ? <span className="font-medium">{r.fields.counterparty}</span> : null}
                          {r.fields?.amount != null ? <span className="tnum text-muted-foreground">{formatKr(r.fields.amount, r.fields.currency)}</span> : null}
                          {r.fields?.date ? <span className="text-muted-foreground">{formatDate(r.fields.date)}</span> : null}
                          <Link to={`/bilag/${r.id}`} className="inline-flex items-center text-primary font-medium hover:underline" data-testid="bilag-queue-open">Åpne <ArrowRight className="h-3 w-3 ml-0.5" /></Link>
                        </div>
                      ) : null}
                    </div>
                    <div className="shrink-0 mt-1.5">
                      {item.status === "laster" ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : null}
                      {item.status === "ferdig" ? <CheckCircle2 className="h-4 w-4 text-success" /> : null}
                      {item.status === "duplikat" ? <CopyCheck className="h-4 w-4 text-muted-foreground" /> : null}
                      {item.status === "feil" ? <AlertTriangle className="h-4 w-4 text-destructive" /> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
